import {
  Alert,
  Button,
  Divider,
  FormControl,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DeclareEmergencyBypassDto,
  EmergencyBypassDetail,
  EmergencyDangerBasis,
  EmergencyEntryPoint,
  EmergencyWorkBy,
} from '@munifocus/contracts-gov';
import {
  EMERGENCY_COMPLIANCE_MAX_DAYS,
  EMERGENCY_DANGER_BASES,
  EMERGENCY_WORK_BY,
} from '@munifocus/contracts-gov';
import {
  useDeclareEmergencyBypass,
  useEmergencyBypasses,
  useRevokeEmergencyBypass,
} from '../lib/emergency-bypass-api';
import { useSubmitValidation } from '../lib/useSubmitValidation';

/**
 * The s. 144(9) emergency bypass, rendered at both of its entry points.
 *
 * The flowchart gives the bypass two exits — Step 5, where the Form 4 still
 * issues on a shortened timeline, and Step 8, where work proceeds to remove the
 * danger — and says they "may proceed simultaneously". So this panel takes the
 * entry point it is mounted at rather than asking, and a file can carry one
 * declaration from each.
 *
 * The compressed compliance period is the officer's decision: s. 144(9) removes
 * the notice and reasonable-hour requirements for inspection and prescribes no
 * compliance period, so the screen says so and requires the reason in writing.
 */

/** Minimum the API accepts for the two justification fields. */
const JUSTIFICATION_MIN = 10;

function DeclarationRow({ row, fileId }: { row: EmergencyBypassDetail; fileId: string }) {
  const { t } = useTranslation();
  const revoke = useRevokeEmergencyBypass(fileId);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const revoked = row.revokedAt !== null;
  const v = useSubmitValidation({
    reason:
      reason.trim().length >= JUSTIFICATION_MIN
        ? null
        : t('common.validation.minLength', { count: JUSTIFICATION_MIN }),
  });

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Typography variant="body2">{t(`gov.emergency.declaredFrom.${row.enteredFrom}`)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {row.declaredByName
          ? t('gov.emergency.declaredBy', {
              date: row.declaredAt.slice(0, 10),
              name: row.declaredByName,
            })
          : t('gov.emergency.declaredOn', { date: row.declaredAt.slice(0, 10) })}
      </Typography>

      <Typography variant="body2">
        {t(`gov.emergency.dangerBasisOption.${row.dangerBasis}`)}
      </Typography>
      <Typography variant="caption">{row.dangerDescription}</Typography>

      {/* The compressed deadline is the practical consequence of the bypass, so
          it is stated outright rather than left to the deadlines dashboard. */}
      {row.complianceDeadline ? (
        <Typography variant="body2" color="error.main">
          {t('gov.emergency.deadline', { date: row.complianceDeadline })}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t('gov.emergency.noDeadline')}
        </Typography>
      )}
      {row.complianceRationale && (
        <Typography variant="caption" color="text.secondary">
          {row.complianceRationale}
        </Typography>
      )}

      {revoked ? (
        // Withdrawn declarations stay on the file — the clock stops, the record
        // does not disappear.
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.emergency.revoked', { reason: row.revokedReason ?? '' })}
        </Alert>
      ) : confirming ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            required
            label={t('gov.emergency.revokeReason')}
            {...v.field('reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            size="small"
            variant="contained"
            disabled={revoke.isPending}
            onClick={v.guard(() => revoke.mutate({ id: row.id, dto: { reason: reason.trim() } }))}
          >
            {t('gov.emergency.revoke')}
          </Button>
          <Button size="small" onClick={() => setConfirming(false)}>
            {t('gov.emergency.cancel')}
          </Button>
        </Stack>
      ) : (
        <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setConfirming(true)}>
          {t('gov.emergency.revoke')}
        </Button>
      )}
    </Stack>
  );
}

function DeclareForm({
  fileId,
  enteredFrom,
  onDone,
}: {
  fileId: string;
  enteredFrom: EmergencyEntryPoint;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const declare = useDeclareEmergencyBypass(fileId);

  const [dangerBasis, setDangerBasis] = useState<EmergencyDangerBasis>('public_safety');
  const [dangerDescription, setDangerDescription] = useState('');
  // Step 8 is the municipality doing the work itself; Step 5 gives the owner a
  // period to comply. Defaulted to the route's usual case, still changeable.
  const [workBy, setWorkBy] = useState<EmergencyWorkBy>(
    enteredFrom === 'step_8' ? 'municipality' : 'owner',
  );
  const [complianceDays, setComplianceDays] = useState('');
  const [complianceRationale, setComplianceRationale] = useState('');
  const [notes, setNotes] = useState('');

  // Parsed rather than truthy-checked: "0" is a non-empty string, so a truthy
  // test reads it as a period of zero and submits it for the API to reject.
  const daysRaw = complianceDays.trim();
  const days = daysRaw === '' ? undefined : Number(daysRaw);
  // The same bounds the DTO and the column enforce. Checked here so an
  // out-of-range period is refused at the input rather than round-tripping into
  // an error toast the officer can do nothing useful with.
  const daysValid =
    days === undefined ||
    (Number.isInteger(days) && days >= 1 && days <= EMERGENCY_COMPLIANCE_MAX_DAYS);
  const needsRationale = days !== undefined;
  const minLength = t('common.validation.minLength', { count: JUSTIFICATION_MIN });
  const v = useSubmitValidation({
    dangerDescription: dangerDescription.trim().length >= JUSTIFICATION_MIN ? null : minLength,
    complianceDays: daysValid
      ? null
      : t('gov.emergency.complianceDaysInvalid', { max: EMERGENCY_COMPLIANCE_MAX_DAYS }),
    complianceRationale:
      !needsRationale || complianceRationale.trim().length >= JUSTIFICATION_MIN ? null : minLength,
  });

  const submit = () =>
    declare.mutate(
      {
        enteredFrom,
        dangerBasis,
        dangerDescription: dangerDescription.trim(),
        workBy,
        ...(days !== undefined
          ? { complianceDays: days, complianceRationale: complianceRationale.trim() }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      } satisfies DeclareEmergencyBypassDto,
      { onSuccess: onDone },
    );

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {declare.isError && <Alert severity="error">{t('gov.emergency.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.emergency.dangerBasis')}</InputLabel>
        <Select
          label={t('gov.emergency.dangerBasis')}
          aria-label={t('gov.emergency.dangerBasis')}
          value={dangerBasis}
          onChange={(e) => setDangerBasis(e.target.value as EmergencyDangerBasis)}
        >
          {EMERGENCY_DANGER_BASES.map((b) => (
            <MenuItem key={b} value={b}>
              {t(`gov.emergency.dangerBasisOption.${b}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary">
        {t('gov.emergency.dangerBasisHint')}
      </Typography>

      <TextField
        fullWidth
        size="small"
        required
        multiline
        minRows={3}
        label={t('gov.emergency.dangerDescription')}
        {...v.field('dangerDescription')}
        helperText={v.errors.dangerDescription ?? t('gov.emergency.dangerDescriptionHint')}
        value={dangerDescription}
        onChange={(e) => setDangerDescription(e.target.value)}
      />

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.emergency.workBy')}</InputLabel>
        <Select
          label={t('gov.emergency.workBy')}
          aria-label={t('gov.emergency.workBy')}
          value={workBy}
          onChange={(e) => setWorkBy(e.target.value as EmergencyWorkBy)}
        >
          {EMERGENCY_WORK_BY.map((w) => (
            <MenuItem key={w} value={w}>
              {t(`gov.emergency.workByOption.${w}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size="small"
        type="number"
        label={t('gov.emergency.complianceDays')}
        // Judged as it is typed (an out-of-range period is plainly wrong), and
        // registered so a submit with it wrong lands the cursor here.
        inputRef={v.register('complianceDays')}
        error={!daysValid}
        helperText={
          daysValid
            ? t('gov.emergency.complianceDaysHint')
            : t('gov.emergency.complianceDaysInvalid', { max: EMERGENCY_COMPLIANCE_MAX_DAYS })
        }
        slotProps={{ htmlInput: { min: 1, max: EMERGENCY_COMPLIANCE_MAX_DAYS } }}
        value={complianceDays}
        onChange={(e) => setComplianceDays(e.target.value)}
      />

      {/* No statute prescribes this period, so the file has to carry the
          officer's reason for choosing it. */}
      {needsRationale && daysValid && (
        <TextField
          fullWidth
          size="small"
          required
          multiline
          minRows={2}
          label={t('gov.emergency.complianceRationale')}
          {...v.field('complianceRationale')}
          helperText={v.errors.complianceRationale ?? t('gov.emergency.complianceRationaleHint')}
          value={complianceRationale}
          onChange={(e) => setComplianceRationale(e.target.value)}
        />
      )}

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label={t('gov.emergency.notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={declare.isPending} onClick={v.guard(submit)}>
          {t('gov.emergency.save')}
        </Button>
        <Button disabled={declare.isPending} onClick={onDone}>
          {t('gov.emergency.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}

export function EmergencyBypassPanel({
  fileId,
  enteredFrom,
}: {
  fileId: string;
  /** Which of the two routes this mount represents. */
  enteredFrom: EmergencyEntryPoint;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useEmergencyBypasses(fileId);
  const [declaring, setDeclaring] = useState(false);

  const rows = data ?? [];
  // Only this route's declarations: the other route has its own mount on its own
  // step, and showing both here would suggest this screen governs both.
  const mine = rows.filter((r) => r.enteredFrom === enteredFrom);
  const liveHere = mine.some((r) => r.revokedAt === null);

  return (
    <HairlineCard sx={{ p: 2 }}>
      <Typography variant="overline">{t('gov.emergency.title')}</Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {t('gov.emergency.hint')}
      </Typography>

      {isError && <Alert severity="error">{t('gov.emergency.loadError')}</Alert>}

      {mine.length === 0 && !declaring && !isLoading && !isError && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('gov.emergency.empty')}
        </Typography>
      )}

      {mine.length > 0 && (
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {mine.map((row) => (
            <DeclarationRow key={row.id} row={row} fileId={fileId} />
          ))}
        </Stack>
      )}

      {declaring ? (
        <DeclareForm fileId={fileId} enteredFrom={enteredFrom} onDone={() => setDeclaring(false)} />
      ) : (
        // One live declaration per route; the other route is declared from its
        // own step, which is what makes the two paths independent.
        !liveHere && (
          <Button size="small" sx={{ mt: 1 }} onClick={() => setDeclaring(true)}>
            {t('gov.emergency.declare')}
          </Button>
        )
      )}
    </HairlineCard>
  );
}
